<script setup lang="ts">
import { onMounted, shallowRef, ref } from "vue";
import { AgGridVue } from "ag-grid-vue3";
import {
  themeQuartz,
  type CellValueChangedEvent,
  type GridApi,
  type GridOptions,
  type GridReadyEvent,
} from "ag-grid-enterprise";
import {useCharterFlightNumbersStore} from "@/charter/stores/charter-flight-numbers-store.ts";
import {FlightNumber, FlightNumberCreateRequest, FlightNumberUpdateRequest} from "@/api/client/models/index.ts";

const flightNumbersStore = useCharterFlightNumbersStore();

const myTheme = themeQuartz.withParams({});

const gridOptions: GridOptions = {
  pagination: true,
  autoSizeStrategy: {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
  defaultColDef: {
    resizable: false,
    filter: true,
  },
  columnDefs: [
    { field: "id", headerName: "ID" },
    {
      field: "from",
      headerName: "From",
      editable: true,
      valueParser: params => params.newValue === "" ? null : Number(params.newValue),
    },
    {
      field: "to",
      headerName: "To",
      editable: true,
      valueParser: params => params.newValue === "" ? null : Number(params.newValue),
    },
    { field: "isFerry", headerName: "Ferry Flight", editable: true, cellEditor: "agCheckboxCellEditor" },
    { field: "isActive", headerName: "Active" },
  ],
};

const gridApi = shallowRef<GridApi | null>(null);
const showAddForm = ref(false);
const newFlightNumber = ref<FlightNumberCreateRequest>({
  from: 0,
  to: 0,
  isFerry: false,
  isActive: true
});
const isSubmitting = ref(false);
const formError = ref<string | null>(null);
const gridError = ref<string | null>(null);

onMounted(async () => {
  await flightNumbersStore.fetchFlightNumbers();
});

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
}

async function onCellValueChanged(event: CellValueChangedEvent<FlightNumber>) {
  const field = event.colDef.field;

  if (!field || event.newValue === event.oldValue || !event.data?.id) {
    return;
  }

  const from = event.data.from == null ? null : Number(event.data.from);
  const to = event.data.to == null ? null : Number(event.data.to);

  if (from == null || to == null || from <= 0 || to <= 0) {
    gridError.value = "Flight numbers must be greater than 0";
    await flightNumbersStore.fetchFlightNumbers();
    return;
  }

  if (from >= to) {
    gridError.value = "The 'From' value must be less than the 'To' value";
    await flightNumbersStore.fetchFlightNumbers();
    return;
  }

  const flightNumberUpdateRequest: FlightNumberUpdateRequest = {
    id: event.data.id,
    from,
    to,
    isFerry: event.data.isFerry,
    isActive: event.data.isActive,
    version: event.data.version,
  };

  try {
    gridError.value = null;
    await flightNumbersStore.updateFlightNumber(flightNumberUpdateRequest);
  } catch (error) {
    gridError.value = error instanceof Error ? error.message : "Failed to update flight number range";
    await flightNumbersStore.fetchFlightNumbers();
  }
}

async function refreshData() {
  await flightNumbersStore.fetchFlightNumbers();
}

function toggleAddForm() {
  showAddForm.value = !showAddForm.value;
  if (showAddForm.value) {
    // Reset form when opening
    newFlightNumber.value = {
      from: 0,
      to: 0,
      isFerry: false,
      isActive: true
    };
    formError.value = null;
  }
}

async function submitNewFlightNumber() {
  if (newFlightNumber.value.from <= 0 || newFlightNumber.value.to <= 0) {
    formError.value = "Flight numbers must be greater than 0";
    return;
  }

  if (newFlightNumber.value.from >= newFlightNumber.value.to) {
    formError.value = "The 'From' value must be less than the 'To' value";
    return;
  }

  try {
    isSubmitting.value = true;
    formError.value = null;
    await flightNumbersStore.addFlightNumber(newFlightNumber.value);
    // Reset form after successful submission
    newFlightNumber.value = {
      from: 0,
      to: 0,
      isFerry: false,
      isActive: true
    };
    // Close the form
    showAddForm.value = false;
  } catch (error) {
    formError.value = error instanceof Error ? error.message : "Failed to add flight number range";
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h2>Flight Number Ranges</h2>
      <div class="flex gap-2">
        <p-button label="Add Flight Number Range" icon="pi pi-plus" severity="primary" outlined @click="toggleAddForm" />
        <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" outlined @click="refreshData" />
      </div>
    </div>

    <!-- Add Flight Number Range Form -->
    <div v-if="showAddForm" class="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <h3 class="mb-3 mt-0">Add New Flight Number Range</h3>
      <div v-if="formError" class="mb-3 rounded-md border-l-4 border-red-500 bg-red-50 p-2 text-sm text-red-500">{{ formError }}</div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div class="space-y-2">
          <label for="from" class="block mb-2">From*</label>
          <p-input-number id="from" v-model="newFlightNumber.from" class="w-full" :disabled="isSubmitting" :min="1" />
        </div>
        <div class="space-y-2">
          <label for="to" class="block mb-2">To*</label>
          <p-input-number id="to" v-model="newFlightNumber.to" class="w-full" :disabled="isSubmitting" :min="1" />
        </div>
        <div class="space-y-2">
          <label for="isFerry" class="block mb-2">Ferry Flight</label>
          <p-checkbox id="isFerry" v-model="newFlightNumber.isFerry" :binary="true" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="isActive" class="block mb-2">Active</label>
          <p-checkbox id="isActive" v-model="newFlightNumber.isActive" :binary="true" :disabled="isSubmitting" />
        </div>
      </div>
      <div class="mt-3 flex justify-end gap-2">
        <p-button label="Cancel" severity="secondary" outlined @click="toggleAddForm" :disabled="isSubmitting" />
        <p-button label="Save" icon="pi pi-save" @click="submitNewFlightNumber" :loading="isSubmitting" />
      </div>
    </div>

    <div v-if="gridError" class="rounded-md border-l-4 border-red-500 bg-red-50 p-2 text-sm text-red-500">
      {{ gridError }}
    </div>

    <AgGridVue
      :rowData="flightNumbersStore.flightNumbers"
      :loading="flightNumbersStore.isLoading"
      :gridOptions="gridOptions"
      :theme="myTheme"
      class="ag-grid-container"
      @cell-value-changed="onCellValueChanged"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<style scoped lang="scss">
.ag-grid-container {
  width: 100%;
  height: 400px;
}

h2, h3 {
  margin: 0 !important;
}

.p-button {
  min-height: fit-content !important;
}

.p-button-icon-left {
  margin-right: 0.4375rem !important;
}

.p-button-label {
  font-weight: 700 !important;
}

.p-button-outlined {
  background-color: white !important;
  color: rgb(30, 41, 59) !important;
  font-size: 14px;
  line-height: 21px !important;
  padding: 6px 10px;
  border-radius: 6px;
  border-color: #e2e8f0 !important;
}

.p-button-outlined:hover {
  background-color: var(--p-button-outlined-secondary-hover-background) !important;
}

.p-button-outlined:active {
  background-color: var(--p-button-outlined-secondary-active-background) !important;
}

label {
  font-weight: 500;
  color: var(--text-color-secondary);
}
</style>
